// Example:
// Integer a = new I|
import {
	ClassCreationTree,
	IdentTree,
	LocalVariableDeclarationListTree,
	LocalVariableDeclarationTree,
	ReferenceTypeTree,
} from "../model";
import {makeCompletionPattern} from "./factory";
import {Completion, CompletionResult} from "@codemirror/autocomplete";
import {EditorView} from "@codemirror/view";
import {Keyword, Misc, Token} from "../lexer";
import {
	anyType,
	instantiateType,
	lookupConstructors,
	ScopeChainNode,
} from "../analysis";

// ArrayList<Integer> a = ne|
const newCtorImmediatelyAssignedToVariable1 = makeCompletionPattern(
	[
		LocalVariableDeclarationListTree,
		LocalVariableDeclarationTree,
		IdentTree,
		Misc.identifier,
	] as const,
	(_chain, scope, varList, variable, _ident, incompleteNewToken) => {
		return completeCtorImmediatelyAssignedToVariable(
			scope,
			varList,
			variable,
			incompleteNewToken
		);
	}
);

// ArrayList<Integer> a = new|
const newCtorImmediatelyAssignedToVariable2 = makeCompletionPattern(
	[
		LocalVariableDeclarationListTree,
		LocalVariableDeclarationTree,
		ClassCreationTree,
		Keyword.new,
	] as const,
	(_chain, scope, varList, variable, _ident, incompleteNewToken) => {
		return completeCtorImmediatelyAssignedToVariable(
			scope,
			varList,
			variable,
			incompleteNewToken
		);
	}
);

// ArrayList<Integer> a = new Ar|
const newCtorImmediatelyAssignedToVariable3 = makeCompletionPattern(
	[
		LocalVariableDeclarationListTree,
		LocalVariableDeclarationTree,
		ClassCreationTree,
		ReferenceTypeTree,
		IdentTree,
		"any",
	] as const,
	(_chain, scope, varList, variable, classCreation) => {
		return completeCtorImmediatelyAssignedToVariable(
			scope,
			varList,
			variable,
			classCreation.newToken
		);
	}
);

function completeCtorImmediatelyAssignedToVariable(
	scope: ScopeChainNode,
	varList: LocalVariableDeclarationListTree,
	variable: LocalVariableDeclarationTree,
	potentiallyIncompleteNewToken: Token
): CompletionResult | null {
	const newTokenIsStartOfRhs =
		potentiallyIncompleteNewToken.span.start === variable.rhs?.span.start;
	if (!newTokenIsStartOfRhs) return null;

	// e.g. new| or ne|
	const newTokenIsValidNew =
		potentiallyIncompleteNewToken.type === Keyword.new ||
		(potentiallyIncompleteNewToken.type === Misc.identifier &&
			"new".startsWith(potentiallyIncompleteNewToken.value));
	if (!newTokenIsValidNew) return null;
	if (variable.cStyleBrackets) return null;

	const targetType = varList.ty;
	if (!targetType || !(targetType instanceof ReferenceTypeTree)) return null;

	const targetTypeName = targetType.prettyPrint(true /* diamond args */);
	const inject = "new " + targetTypeName + "()";

	const referent = targetType.referent;
	if (!(referent?.type === "class")) return null;

	const candidateCtors = lookupConstructors(
		scope,
		instantiateType(referent ?? anyType())
	);
	const atLeastOneCtorWithArguments =
		candidateCtors?.some((ctor) => ctor.val.arguments.length > 0) ?? false;

	let moveCursorTo =
		inject.length - 1 + potentiallyIncompleteNewToken.span.start;
	if (!atLeastOneCtorWithArguments) moveCursorTo++ /* after parentheses */;

	return {
		from: potentiallyIncompleteNewToken.span.start,
		options: [
			{
				label: inject,
				type: "function",
				apply: (
					view: EditorView,
					completion: Completion,
					from: number,
					to: number
				) => {
					view.dispatch(
						view.state.update({
							changes: {from, to, insert: inject},
							selection: {
								anchor: moveCursorTo,
								head: moveCursorTo,
							},
						})
					);
				},
			},
		],
	};
}

export const EXPRESSIONS = [
	newCtorImmediatelyAssignedToVariable1,
	newCtorImmediatelyAssignedToVariable2,
	newCtorImmediatelyAssignedToVariable3,
];
